import React, { useCallback, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import styles from './ClientSidebar.module.css';

import collapsedLogo from '../../../assets/Logo + Icon.png';
import expandedLogo from '../../../assets/covi_standlogo_white.png';
import pinnedLogo from '../../../assets/Covi_pinned_logo.png';
import { useUserName } from "../../../services/user_info";
import { ArchiveBoxIcon, ChartPieIcon, CurrencyDollarIcon, Squares2X2Icon, UserIcon } from '@heroicons/react/20/solid';

interface MenuItem {
    id: string;
    label: string;
    icon: React.ForwardRefExoticComponent<React.SVGProps<SVGSVGElement>>;
    path: string;
    hasState?: boolean;
}

// 사이드바 메뉴 아이템
const menuItems: MenuItem[] = [
    {
        id: 'dashboard',
        label: 'Main Dashboard',
        icon: ChartPieIcon,
        path: '/client',
        hasState: true
    },
    {
        id: 'projects',
        label: 'Projects',
        icon: Squares2X2Icon,
        path: '/client/projects'
    },
    {
        id: 'documents',
        label: 'Documents',
        icon: ArchiveBoxIcon,
        path: '/client/documents'
    },
    {
        id: 'billing',
        label: 'Billing',
        icon: CurrencyDollarIcon,
        path: '/client/billing'
    }
];

interface ClientSidebarProps {
    className?: string;
    onExpand?: (expanded: boolean) => void;
    onToggle?: () => void;
    onMenuClick?: () => void;
    isFirstTempLogin?: boolean;
    isExpanded?: boolean;
    isLocked?: boolean;
    isMobile?: boolean;
    isInitialized?: boolean;
}

// 클라이언트 사이드바
const ClientSidebar: React.FC<ClientSidebarProps> = ({
                                                         className = '',
                                                         onExpand,
                                                         onToggle,
                                                         onMenuClick,
                                                         isFirstTempLogin = false,
                                                         isExpanded = false,
                                                         isLocked = false,
                                                         isMobile = false,
                                                         isInitialized = false
                                                     }) => {
    const navigate = useNavigate();
    const location = useLocation();
    const userName = useUserName();

    // 현재 메뉴 반환
    const isMenuActive = useCallback((path: string) => {
        if (path === '/client') {
            return location.pathname === path;
        }
        return location.pathname.startsWith(path);
    }, [location.pathname]);

    // 메뉴 이동
    const handleNavigate = useCallback(async (path: string, hasState?: boolean) => {
        // 모바일에서 메뉴 클릭 시 사이드바 닫기
        onMenuClick?.();

        try {
            const navigationOptions = hasState ? { state: { isFirstTempLogin } } : undefined;
            navigate(path, navigationOptions);
        } catch (error) {
            console.error('Navigation failed:', error);
            navigate(path);
        }
    }, [navigate, onMenuClick, isFirstTempLogin]);

    // 펼치기 (PC에서만 마우스 hover 시, 잠금되지 않은 경우에만)
    const handleExpand = useCallback((expanded: boolean) => {
        if (!isMobile && !isLocked) {
            onExpand?.(expanded);
        }
    }, [isMobile, isLocked, onExpand]);

    // 로고 클릭 처리 (PC에서만 잠금 토글)
    const handleLogoClick = useCallback(() => {
        if (isMobile) return;

        onToggle?.();
    }, [isMobile, isLocked, onToggle]);

    // PC에서 계정 메뉴 클릭 시에는 사이드바 상태 유지
    const handleAccountClick = useCallback(() => {
        navigate('/client/account');
        // PC에서는 사이드바 상태 유지, 모바일에서만 닫기
        if (isMobile) {
            onMenuClick?.();
        }
    }, [navigate, isMobile, onMenuClick]);

    // 사이드바 클래스명 계산
    const sidebarClassName = useMemo(() => {
        const classes = [styles.sidebar];
        if (isExpanded) classes.push(styles.expanded);
        else classes.push(styles.collapsed);
        if (!isInitialized) classes.push(styles.noTransition);
        if (className) classes.push(className);
        return classes.join(' ');
    }, [isExpanded, isInitialized, className]);

    // 로고 클래스명 계산
    const logoClassName = useMemo(() => {
        const classes = [styles.logo];
        if (isLocked) classes.push(styles.locked);
        return classes.join(' ');
    }, [isLocked]);

    return (
        <div
            className={sidebarClassName}
            onMouseEnter={() => handleExpand(true)}
            onMouseLeave={() => handleExpand(false)}
        >
            {/* 고정 아이콘 (PC에서만 클릭 가능) */}
            <div
                className={logoClassName}
                onClick={handleLogoClick}
                style={{ cursor: isMobile ? 'default' : 'pointer' }}
                role={isMobile ? undefined : "button"}
                tabIndex={isMobile ? undefined : 0}
                onKeyDown={isMobile ? undefined : (e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        handleLogoClick();
                    }
                }}
                title={isMobile ? undefined : (isLocked ? "사이드바 잠금 해제" : "사이드바 잠금")}
            >
                <img
                    src={isLocked ? pinnedLogo : (isExpanded ? expandedLogo : collapsedLogo)}
                    alt="Logo"
                    className={`${styles.logoImage} ${
                        isLocked ? styles.pinnedLogo :
                            (isExpanded ? styles.expandedLogo : styles.collapsedLogo)
                    }`}
                />
            </div>

            <nav className={styles.navigation}>
                {/* 사이드바 메뉴 */}
                {menuItems.map((item) => (
                    <button
                        key={item.id}
                        onClick={() => handleNavigate(item.path, item.hasState)}
                        className={`${styles.menuItem} ${isMenuActive(item.path) ? styles.active : ''}`}
                        title={!isExpanded && !isMobile ? item.label : undefined}
                    >
                        <span className={styles.icon}>
                            <item.icon className={styles.iconImage} />
                        </span>
                        {isExpanded && <span className={styles.label}>{item.label}</span>}
                    </button>
                ))}
            </nav>

            {/* 계정 메뉴 */}
            <div className={styles.userSection}>
                <button
                    className={`${styles.menuItem} ${location.pathname === '/client/account' ? styles.active : ''}`}
                    onClick={handleAccountClick}
                    title={!isExpanded && !isMobile ? userName || 'Account' : undefined}
                >
                    <span className={styles.icon}>
                        <UserIcon className={styles.iconImage} />
                    </span>
                    {isExpanded && <span className={styles.label}>{userName}</span>}
                </button>
            </div>
        </div>
    );
};

export default ClientSidebar;